import { BackendJourneyConversion, Workflow, Node } from 'modules/workflows/types';
import { Goals24 } from 'ui/icons/aurora/24px/studio';
import { sendNodeUtils } from 'modules/workflows/utils';
import { DateFormat, localizeDateTime } from 'utils/l10n';
import { useGetJourneyConversionsMetricsForCampaigns } from 'modules/journey/hooks/useGetJourneyConversionsMetricsForCampaigns/useGetJourneyConversionsMetricsForCampaigns';
import { useEffect } from 'react';
import { InfoFill16 } from 'ui/icons';
import { body2, borderRadius, colors, spacing } from 'ui/theme';
import { getFlexMetricsDateRange, getJourneySendTiles } from 'modules/workflows/ducks';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { AnalyticsCard } from './AnalyticsCard';
import {
  ContactsContainer,
  ConversionDateText,
  JourneyPanelBody,
  JourneyPanelTitle,
  SelectedConversionContainer,
  StyledSpinner,
  StyledTitleText,
} from './styled';
import { NodeConversionsCard } from './NodeAnalyticsCard';
import { ConversionCounts } from './ContactCounts/ConversionCounts';
import { getNodesCampaignsIds } from './WorkflowAnalytics.utils';

export const HasNoCampaignsContainer = styled.div`
  display: flex;
  padding: ${spacing.threeQuarter};
  align-items: flex-start;
  gap: ${spacing.threeQuarter};
  align-self: stretch;

  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.borderPrimary};
  background: ${colors.backgroundTertiary};
`;

export const HasNoCampaignsBody = styled.div`
  align-self: stretch;
  ${body2}
`;

export const TitleWrapper = styled.div`
  display: flex;
  gap: ${spacing.quarter};
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledAnalyticsCard = styled(AnalyticsCard)`
  background-color: transparent;
  border-radius: 0;
  box-shadow: none;
`;

export const CountContainer = styled.div`
  width: 100%;
  padding-bottom: ${spacing.base};
`;

export const JourneyConversionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const JOURNEY_CONVERSION_CARD_TEST_IDS = {
  CONTAINER: 'journey-conversion-card-container',
  NO_CAMPAIGNS: `journey-has-no-campaigns`,
  LOADING_CONVERSIONS: 'conversions-card-loading-spinner',
  JOURNEY_CONVERSION_EVENT: 'conversions-card-title',
};

const JourneyHasNoCampaigns = () => {
  return (
    <HasNoCampaignsContainer data-test={JOURNEY_CONVERSION_CARD_TEST_IDS.NO_CAMPAIGNS}>
      <InfoFill16 />
      <HasNoCampaignsBody>
        A conversion event has been selected for this journey, but campaigns are needed to track its
        progress.
      </HasNoCampaignsBody>{' '}
    </HasNoCampaignsContainer>
  );
};

interface JourneyConversionCardProps {
  /** The current workflow */
  workflow: Workflow;
  /** The journey conversion attached to the journey */
  journeyConversion: BackendJourneyConversion;
  /** Node to render on the selected node card */
  node?: Node;
}

const getFormattedCreatedAt = (createdAt: string) => {
  const date = new Date(createdAt);
  return localizeDateTime(date, {
    dateStyle: DateFormat.Numeric,
  });
};

export const JourneyConversionCard = ({
  node,
  workflow,
  journeyConversion,
}: JourneyConversionCardProps) => {
  const { getJourneyConversionsMetricsForCampaigns, isLoading, journeyConversionData } =
    useGetJourneyConversionsMetricsForCampaigns();
  const nodes = useSelector(getJourneySendTiles);
  const campaignIds = getNodesCampaignsIds(nodes);
  const flexRange = useSelector(getFlexMetricsDateRange);
  const { endDateString, startDateString } = flexRange;
  const hasJourneySendTiles = nodes.length > 0;

  const conversionEvent = journeyConversion?.conversions?.[0];
  const conversionEventName = conversionEvent?.eventName;
  const conversionAddedDate = journeyConversion?.createdAt;
  const isSendTile = node && sendNodeUtils.isSendNode(node?.type);

  useEffect(() => {
    if (workflow?.id && hasJourneySendTiles) {
      getJourneyConversionsMetricsForCampaigns(
        workflow.id,
        campaignIds,
        startDateString as string,
        endDateString as string,
      );
    }
  }, [workflow?.id, startDateString, endDateString]);

  const conversionValue = journeyConversionData?.[conversionEventName];

  return (
    <JourneyConversionsContainer>
      <ContactsContainer>
        <JourneyPanelTitle>
          <TitleWrapper>
            <Goals24 />
            <TitleContainer>
              <StyledTitleText
                data-test={JOURNEY_CONVERSION_CARD_TEST_IDS.JOURNEY_CONVERSION_EVENT}
              >
                {conversionEventName}
              </StyledTitleText>
            </TitleContainer>
          </TitleWrapper>
        </JourneyPanelTitle>
        <JourneyPanelBody>
          <CountContainer>
            {!hasJourneySendTiles ? (
              <JourneyHasNoCampaigns />
            ) : (
              (isLoading && (
                <StyledSpinner data-test={JOURNEY_CONVERSION_CARD_TEST_IDS.LOADING_CONVERSIONS} />
              )) || (
                <>
                  <ConversionCounts totalConversion={conversionValue} />
                </>
              )
            )}
            {conversionAddedDate && (
              <ConversionDateText>
                Journey conversion added {getFormattedCreatedAt(conversionAddedDate)}
              </ConversionDateText>
            )}
          </CountContainer>
        </JourneyPanelBody>
      </ContactsContainer>
      <>
        {isSendTile && (
          <SelectedConversionContainer>
            <NodeConversionsCard {...{ node, journeyConversion, workflow }} />
          </SelectedConversionContainer>
        )}
      </>
    </JourneyConversionsContainer>
  );
};
